import { env } from '@kc-console/context';
import { getAccountBaseUrl, STATUS_CODE } from '@kc-console/core';

import { CookieKeyMap } from '../constants/cookie';
import { appendContextCookies } from './cookie/appendContextCookies';
import { expireSetCookieString } from './cookie/expireSetCookieString';
import { getRequestUrlData } from './getRequestUrlData';

import type { GetServerSidePropsContext } from 'next';
import type { AxiosError } from 'axios';
import type { ServiceAppState } from '@kc-console/context';
import type { StatusCode } from '@kc-console/core';

interface CommonGuardErrorHandlerParams {
  context: GetServerSidePropsContext;
  error: unknown;
  state: Partial<ServiceAppState>;
}

/**
 * 가드에서 공통으로 사용되는 에러핸들러입니다.
 * @description
 * - status가 없는 경우 서버 에러로 간주합니다.
 * - 401 Unauthorized 에러의 경우 토큰이 유효하지 않거나 없는 경우 로그아웃 후 로그인 페이지로 이동합니다.
 * - 그 외의 에러의 경우 axios error status code를 그대로 반환합니다.
 * - 참고 : 공통 에러 핸들링이 필요한 경우에만 사용합니다.
 * @example
 * ```tsx
 * const testGuard: Guard = async ({ context, state }) => {
 *   try {
 *    // guard logic
 *   }
 *   catch (error) {
 *     if () {} // 가드 개별 에러 핸들링이 필요할경우 추가
 *     return commonGuardErrorHandler({ error, state, context });
 *   }
 * }
 * ```
 */
export const commonGuardErrorHandler = ({
  context,
  error,
  state,
}: CommonGuardErrorHandlerParams): Partial<ServiceAppState> => {
  const iamEndpoint = env.KC_IAM_ENDPOINT ?? '';
  const token = state.token ?? '';
  const refinedError = error as AxiosError;
  /**
   * status가 없을 경우 서버 에러로 간주
   */
  if (!refinedError?.status) {
    return {
      status: STATUS_CODE.INTERNAL_SERVER_ERROR,
    };
  }

  /**
   * 401 Unauthorized 에러의 경우
   * 현재 가지고 있는 토큰이 없음 : redirect 로그인 페이지
   * 현재 가지고 있는 토큰이 유효하지 않음 : KC_AUT 삭제 -> 로그아웃 -> redirect 로그인 페이지
   */
  if (refinedError.status === STATUS_CODE.UNAUTHORIZED) {
    const { href, urlObj } = getRequestUrlData(context);
    const redirectEndpoint = getAccountBaseUrl(urlObj.origin);
    const authToken = token || context.req.cookies[CookieKeyMap.TOKEN_KEY];

    if (authToken && authToken.length > 0) {
      appendContextCookies(context, [
        expireSetCookieString(CookieKeyMap.TOKEN_KEY, {
          path: '/',
          secure: true,
        }),
      ]);

      return {
        redirect: {
          destination: `${iamEndpoint}/auth/logout?redirect_uri=${redirectEndpoint}`,
          permanent: false,
        },
      };
    }

    return {
      redirect: {
        destination: `${redirectEndpoint}?redirect_uri=${encodeURIComponent(href)}`,
        permanent: false,
      },
    };
  }

  /**
   * 나머지 경우는 axios error status code를 그대로 반환
   */
  return {
    status: (refinedError.status as StatusCode) ?? STATUS_CODE.INTERNAL_SERVER_ERROR,
  };
};
